// lib/mockData.ts
// Mock data for demo mode - matches AI Studio prototype and new database schema
import type {
  Building,
  Unit,
  UnitType,
  Testimonial,
  ReferralCode,
  BuildingImage
} from '../types/database';

export const MOCK_UNIT_TYPES: UnitType[] = [
  { id: 'ut-bachelor', name: 'Bachelor', sort_order: 1 },
  { id: 'ut-jr1br', name: 'Jr 1-BR', sort_order: 2 },
  { id: 'ut-1br', name: '1-BR', sort_order: 3 },
  { id: 'ut-1br-den', name: '1BR+Den', sort_order: 4 },
  { id: 'ut-2br', name: '2-BR', sort_order: 5 },
  { id: 'ut-3br', name: '3-BR', sort_order: 6 },
];

export const MOCK_BUILDING: Building = {
  id: 'bld-66-broadway',
  name: '66 Broadway Avenue',
  address: '66 Broadway Avenue',
  city: 'Toronto',
  postal_code: 'M4P 1T4',
  description: 'Located in the heart of Yonge & Eglinton, this 20-story building offers modern amenities and spacious units with stunning city views. Recently renovated common areas and 24/7 concierge service.',
  neighborhood_description: 'Yonge-Eglinton is one of Toronto\'s most vibrant neighbourhoods, known as "Young and Eligible" for its mix of professionals and excellent transit. The area offers world-class dining, shopping, and entertainment steps from your door.',
  amenities: ['Outdoor Pool', 'Fitness Centre', 'Laundry Room', 'Underground Parking', '24/7 Concierge', 'Rooftop Deck', 'Party Room', 'Bike Storage'],
  latitude: 43.7085,
  longitude: -79.3986,
  walk_score: 92,
  walk_score_description: "Walker's Paradise",
  transit_score: 95,
  transit_score_description: 'Excellent Transit',
  bike_score: 78,
  bike_score_description: 'Very Bikeable',
  walk_score_updated_at: new Date().toISOString(),
  leasing_email: 'leasing@beauxproperties.ca',
  leasing_phone: '416-555-0123',
  newcomer_friendly: true,
  accepts_proof_of_funds: true,
  languages_spoken: ['English', 'French', 'Mandarin', 'Cantonese'],
  accessibility: {
    wheelchair_accessible: true,
    elevator_access: true,
    step_free_access: true,
    accessible_parking: true,
    visual_aids: false,
    hearing_assistance: false,
    service_animals: true,
    accessible_units_available: true,
    additional_notes: 'Two elevators service all floors. Accessible parking available in underground garage.',
  },
  nearby_transit: [
    { name: 'Eglinton Station', type: 'subway', walk_minutes: 2, lines: ['Line 1 Yonge-University'] },
    { name: 'Yonge St Bus', type: 'bus', walk_minutes: 1 },
  ],
  nearby_amenities: [
    { name: 'Whole Foods', type: 'grocery', walk_minutes: 3 },
    { name: 'Yonge Eglinton Centre', type: 'shopping', walk_minutes: 2 },
    { name: 'Eglinton Park', type: 'park', walk_minutes: 5 },
    { name: 'Sunnybrook Hospital', type: 'hospital', walk_minutes: 10 },
  ],
  current_promotion: {
    title: 'Winter Special - First Month FREE',
    description: 'First month FREE on 12+ month leases',
    end_date: '2025-03-31',
    terms: 'Available on select units. Must sign by March 31, 2025.',
  },
  status_updates: [
    {
      id: 'su-1',
      title: 'Elevator #2 Maintenance',
      content: 'Scheduled maintenance Dec 28-29. Elevator #1 remains operational.',
      type: 'warning',
      is_active: true,
      created_at: '2024-12-15',
    },
    {
      id: 'su-2',
      title: 'Lobby Renovation Complete',
      content: 'New furniture and lighting installed in main lobby.',
      type: 'success',
      is_active: true,
      created_at: '2024-12-10',
    },
  ],
  slug: '66-broadway',
  is_active: true,
  hero_image_url: 'https://images1.apartments.com/i2/um8rebHEA-1Pt2btNEsPMGDY3blfbCFKjq8SBT6Cj8k/111/algonquin-apartments-toronto-on-primary-photo.jpg',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

export const MOCK_UNITS: Unit[] = [
  {
    id: 'unit-1204',
    building_id: 'bld-66-broadway',
    unit_type_id: 'ut-1br',
    unit_number: '1204',
    floor: 12,
    price: 2150,
    square_feet: 650,
    availability_date: '2025-01-15',
    is_available: true,
    features: ['In-suite laundry', 'Air conditioning', 'Balcony', 'Hardwood floors', 'Dishwasher', 'Stainless steel appliances'],
    virtual_tour_url: 'https://my.matterport.com/show/?m=example123',
    utilities_included: false,
    utilities_estimate: 75,
    parking_cost: 175,
    key_deposit: null,
    accessibility_features: [],
    promotion: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by: 'system',
    unit_type: { id: 'ut-1br', name: '1-BR', sort_order: 3 },
    images: [],
  },
  {
    id: 'unit-803',
    building_id: 'bld-66-broadway',
    unit_type_id: 'ut-2br',
    unit_number: '803',
    floor: 8,
    price: 2695,
    square_feet: 900,
    availability_date: '2025-02-01',
    is_available: true,
    features: ['Ensuite bathroom', 'Walk-in closet', 'Balcony', 'New appliances', 'Corner unit', 'City views'],
    virtual_tour_url: null,
    utilities_included: false,
    utilities_estimate: 100,
    parking_cost: 175,
    key_deposit: null,
    accessibility_features: [],
    promotion: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by: 'system',
    unit_type: { id: 'ut-2br', name: '2-BR', sort_order: 5 },
    images: [],
  },
  {
    id: 'unit-315',
    building_id: 'bld-66-broadway',
    unit_type_id: 'ut-bachelor',
    unit_number: '315',
    floor: 3,
    price: 1750,
    square_feet: 440,
    availability_date: new Date().toISOString().split('T')[0], // Today = Available Now
    is_available: true,
    features: ['Murphy bed space', 'Renovated kitchen', 'Large window', 'Utilities included'],
    virtual_tour_url: null,
    utilities_included: true,
    utilities_estimate: null,
    parking_cost: 150,
    key_deposit: null,
    accessibility_features: [],
    promotion: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by: 'system',
    unit_type: { id: 'ut-bachelor', name: 'Bachelor', sort_order: 1 },
    images: [],
  },
  {
    id: 'unit-1501',
    building_id: 'bld-66-broadway',
    unit_type_id: 'ut-2br',
    unit_number: '1501',
    floor: 15,
    price: 2950,
    square_feet: 1050,
    availability_date: '2025-01-20',
    is_available: true,
    features: ['Penthouse floor', 'Panoramic views', '2 full bathrooms', 'Den area', 'Premium finishes', 'In-suite laundry'],
    virtual_tour_url: 'https://my.matterport.com/show/?m=example456',
    utilities_included: false,
    utilities_estimate: 120,
    parking_cost: 200,
    key_deposit: 500,
    accessibility_features: ['Wide doorways', 'Roll-in shower'],
    promotion: 'First month free!',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by: 'system',
    unit_type: { id: 'ut-2br', name: '2-BR', sort_order: 5 },
    images: [],
  },
  {
    id: 'unit-607',
    building_id: 'bld-66-broadway',
    unit_type_id: 'ut-1br-den',
    unit_number: '607',
    floor: 6,
    price: 2350,
    square_feet: 750,
    availability_date: '2025-02-15',
    is_available: true,
    features: ['Separate den/office', 'Updated bathroom', 'Parquet floors', 'Balcony'],
    virtual_tour_url: null,
    utilities_included: false,
    utilities_estimate: 85,
    parking_cost: 175,
    key_deposit: null,
    accessibility_features: [],
    promotion: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by: 'system',
    unit_type: { id: 'ut-1br-den', name: '1BR+Den', sort_order: 4 },
    images: [],
  },
];

export const MOCK_TESTIMONIALS: Testimonial[] = [
  {
    id: 'test-1',
    building_id: 'bld-66-broadway',
    resident_name: 'Sarah M.',
    content: 'The location is unbeatable - I walk to work in 10 minutes and have everything I need within steps. The building staff are incredibly responsive and friendly.',
    rating: 5,
    move_in_year: 2021,
    is_featured: true,
    is_approved: true,
    created_at: new Date().toISOString(),
  },
  {
    id: 'test-2',
    building_id: 'bld-66-broadway',
    resident_name: 'James L.',
    content: 'Moving from Vancouver, I was nervous about finding a good place. The team here made it so easy, even helping me with the Ontario rental process. Highly recommend for newcomers!',
    rating: 5,
    move_in_year: 2023,
    is_featured: true,
    is_approved: true,
    created_at: new Date().toISOString(),
  },
  {
    id: 'test-3',
    building_id: 'bld-66-broadway',
    resident_name: 'Maria C.',
    content: 'Great value for Yonge and Eglinton. The pool is a huge bonus in summer, and the gym has everything I need. Maintenance requests are handled quickly.',
    rating: 4,
    move_in_year: 2022,
    is_featured: false,
    is_approved: true,
    created_at: new Date().toISOString(),
  },
  {
    id: 'test-4',
    building_id: 'bld-66-broadway',
    resident_name: 'David K.',
    content: 'The den is perfect for my home office setup. Building is well-maintained and quiet. Underground parking is a lifesaver in winter.',
    rating: 5,
    move_in_year: 2024,
    is_featured: false,
    is_approved: true,
    created_at: new Date().toISOString(),
  },
];

export const MOCK_REFERRAL_CODES: ReferralCode[] = [
  {
    id: 'ref-1',
    code: 'SARAH2024',
    tenant_name: 'Sarah M.',
    tenant_email: 'sarah.m@example.com',
    building_id: 'bld-66-broadway',
    reward_amount: 250,
    is_active: true,
    created_at: new Date().toISOString(),
  },
];

export const MOCK_BUILDING_IMAGES: BuildingImage[] = [
  {
    id: 'bimg-1',
    building_id: 'bld-66-broadway',
    image_url: 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=800',
    image_type: 'pool',
    caption: 'Outdoor Pool',
    sort_order: 1,
    created_at: new Date().toISOString(),
  },
  {
    id: 'bimg-2',
    building_id: 'bld-66-broadway',
    image_url: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800',
    image_type: 'gym',
    caption: 'Fitness Centre',
    sort_order: 2,
    created_at: new Date().toISOString(),
  },
  {
    id: 'bimg-3',
    building_id: 'bld-66-broadway',
    image_url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800',
    image_type: 'lobby',
    caption: 'Renovated Lobby',
    sort_order: 3,
    created_at: new Date().toISOString(),
  },
];

// Placeholder images (will be replaced with client assets)
export const PLACEHOLDER_IMAGES = {
  building: 'https://images1.apartments.com/i2/um8rebHEA-1Pt2btNEsPMGDY3blfbCFKjq8SBT6Cj8k/111/algonquin-apartments-toronto-on-primary-photo.jpg',
  livingRoom: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800',
  kitchen: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800',
  bedroom: 'https://images.unsplash.com/photo-1616594039964-ae9021a400a0?w=800',
  bathroom: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800',
  balcony: 'https://images.unsplash.com/photo-1502005229762-cf1b2da7c5d6?w=800',
  pool: 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=800',
  gym: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800',
};

// Get unit photos (for demo)
export function getUnitPhotos(_unitType: string) {
  return [
    { label: 'Living Room', url: PLACEHOLDER_IMAGES.livingRoom, type: 'living_room' },
    { label: 'Kitchen', url: PLACEHOLDER_IMAGES.kitchen, type: 'kitchen' },
    { label: 'Bedroom', url: PLACEHOLDER_IMAGES.bedroom, type: 'bedroom' },
    { label: 'Bathroom', url: PLACEHOLDER_IMAGES.bathroom, type: 'bathroom' },
    { label: 'Balcony', url: PLACEHOLDER_IMAGES.balcony, type: 'balcony' },
    { label: 'Outdoor Pool', url: PLACEHOLDER_IMAGES.pool, type: 'other' },
    { label: 'Fitness Center', url: PLACEHOLDER_IMAGES.gym, type: 'other' },
  ];
}

// Helper to find unit type by ID
export function getUnitTypeById(id: string): UnitType | undefined {
  return MOCK_UNIT_TYPES.find(ut => ut.id === id);
}

// Helper to get available units
export function getAvailableUnits(): Unit[] {
  return MOCK_UNITS.filter(u => u.is_available);
}

// Helper to find unit by ID
export function getUnitById(id: string): Unit | undefined {
  return MOCK_UNITS.find(u => u.id === id);
}

// Helper to get featured testimonials
export function getFeaturedTestimonials(): Testimonial[] {
  return MOCK_TESTIMONIALS.filter(t => t.is_featured && t.is_approved);
}
